import assert from 'minimalistic-assert'

import createLock from './lock.js'
import { toMS } from './utils.js'

class LocalSeedBackups {
  #eventLog
  #localSeedBackupsAtom
  #localSeedPrivateBackupsAtom
  #localSeedBackupMaxAgeAtom
  #lock

  constructor({
    eventLog,
    localSeedPrivateBackupsAtom,
    localSeedBackupsAtom,
    localSeedBackupMaxAgeAtom,
  }) {
    this.#eventLog = eventLog
    this.#localSeedBackupsAtom = localSeedBackupsAtom
    this.#localSeedPrivateBackupsAtom = localSeedPrivateBackupsAtom
    this.#localSeedBackupMaxAgeAtom = localSeedBackupMaxAgeAtom
    this.#lock = createLock()
  }

  #withLock = async (fn) => {
    await this.#lock.acquire()
    try {
      return await fn()
    } finally {
      this.#lock.release()
    }
  }

  add = ({ mnemonic, dateBackedUp = Date.now(), dateCreated = Date.now() }) =>
    this.#withLock(async () => {
      const items = await this.#localSeedPrivateBackupsAtom.get()

      const nextItems = [...items]

      const index = nextItems.findIndex((item) => item.mnemonic === mnemonic)

      if (index >= 0) {
        const existing = nextItems[index]

        nextItems.splice(index, 1, { ...existing, dateBackedUp })
      } else {
        nextItems.push({
          mnemonic,
          dateCreated,
          dateBackedUp,
        })
      }

      await this.#localSeedPrivateBackupsAtom.set(nextItems)
    })

  get = async (index) => {
    const items = await this.#localSeedPrivateBackupsAtom.get()

    return items[index]
  }

  // Caller must hold the lock. Exists so lock-holding methods (e.g. clearOldBackups)
  // can remove without re-acquiring the non-reentrant lock and deadlocking.
  #removeWithoutLock = async (indices) => {
    const items = await this.#localSeedPrivateBackupsAtom.get()

    const nextItems = items.filter((item, index) => !indices.includes(index))

    await this.#localSeedPrivateBackupsAtom.set(nextItems)
  }

  remove = (indices) => this.#withLock(() => this.#removeWithoutLock(indices))

  setBackupMaxAge = async ({ unit, count }) => {
    assert(
      ['month', 'second'].includes(unit) && typeof count === 'number',
      'invalid seed max backup age'
    )

    await this.#localSeedBackupMaxAgeAtom.set({ unit, count })
  }

  clear = () =>
    this.#withLock(async () => {
      await this.#localSeedPrivateBackupsAtom.set(undefined)
    })

  clearOldBackups = () =>
    this.#withLock(async () => {
      const value = await this.#localSeedBackupMaxAgeAtom.get()
      const maxAge = toMS(value)
      const backups = await this.#localSeedBackupsAtom.get()

      if (backups.length === 0) return

      const toDelete = backups.filter(
        ({ dateBackedUp, isCurrent }) => Date.now() - dateBackedUp > maxAge && !isCurrent
      )

      toDelete.forEach(({ dateBackedUp }) =>
        this.#eventLog?.record({
          event: 'backup_phrase_expired',
          maxAge,
          age: Date.now() - dateBackedUp,
        })
      )

      if (toDelete.length > 0) {
        const indices = toDelete.map((backup) => backup.index)
        await this.#removeWithoutLock(indices)
      }
    })
}

const createLocalSeedBackups = (opts) => new LocalSeedBackups(opts)

const localSeedBackupsDefinition = {
  id: 'localSeedBackups',
  type: 'module',
  factory: createLocalSeedBackups,
  dependencies: [
    'localSeedBackupsAtom',
    'localSeedPrivateBackupsAtom',
    'localSeedBackupMaxAgeAtom',
    'eventLog?',
  ],
  public: true,
}

export default localSeedBackupsDefinition
